import React, {useEffect, useLayoutEffect, useMemo, useState} from 'react';
import {
  NativeScrollEvent,
  NativeSyntheticEvent,
  Platform,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import Animated, {FadeInRight} from 'react-native-reanimated';
import useHomeScreenAnimation from './useHomeScreenAnimation';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import {useNavigation} from '@react-navigation/native';
import {NavigationProps} from '~types/navigation/navigation.type';
import useTabBar from '~containers/Core/navigation/AppScreens/zustand';
import {useTranslation} from 'react-i18next';
import PersonalEvents from './tabComponents/PersonalEvents';
import {useGetUserType} from '~hooks/event/useGetUserType';
import Svg, {Path} from 'react-native-svg';
import {widthPercentageToDP as wp, heightPercentageToDP as hp} from 'react-native-responsive-screen';
import {useTheme} from '~contexts/ThemeContext';
import {useUserStore} from '~providers/userStore/zustand';

const MyEventTabs = () => {
  const {colors} = useTheme();
  const {t} = useTranslation();
  const {top} = useSafeAreaInsets();
  const [isMapView, setIsMapView] = useState<boolean>(true);

  const eventTypes = [
    {
      id: 1,
      title: t('home.upcoming'),
      component: (
        scrollHandler: (event: NativeSyntheticEvent<NativeScrollEvent>) => void,
        filterEvents: (data: any, selectedTimeframe: {id: number; title: string}) => any,
        selectedTimeframe: {id: number; title: string},
        globalInputValue: string,
      ) => (
        <PersonalEvents
          scrollHandler={scrollHandler}
          filterEvents={filterEvents}
          selectedTimeframe={selectedTimeframe}
          globalInputValue={globalInputValue}
          timeframe={null}
          type={'booked_upcoming'}
        />
      ),
    },
    {
      id: 2,
      title: t('home.past'),
      component: (
        scrollHandler: (event: NativeSyntheticEvent<NativeScrollEvent>) => void,
        filterEvents: (data: any, selectedTimeframe: {id: number; title: string}) => any,
        selectedTimeframe: {id: number; title: string},
        globalInputValue: string,
      ) => (
        <PersonalEvents
          scrollHandler={scrollHandler}
          filterEvents={filterEvents}
          selectedTimeframe={selectedTimeframe}
          globalInputValue={globalInputValue}
          timeframe={null}
          type={'booked_past'}
        />
      ),
    },
  ];

  // Simplified timeframes - not used anymore since we control it via event types
  const timeframes = [
    {
      id: 1,
      title: t('home.upcoming'),
    },
    {
      id: 2,
      title: t('home.past'),
    },
  ];

  const {setIsTabBarDisabled} = useTabBar();
  const user = useUserStore(state => state.user);
  const {data: userType} = useGetUserType(user?.uid!);
  const navigation = useNavigation<NavigationProps>();

  const [selectedEventType, setSelectedEventType] = useState(eventTypes[0]);
  const [selectedTimeframe, setSelectedTimeframe] = useState(timeframes[0]);
  const {scrollHandler, scrollAnimatedStyle, addEventButtonStyle} = useHomeScreenAnimation();

  const [globalInputValue, setGlobalInputValue] = useState('');

  // console.log('dynamicStartDate', dynamicStartDate);
  // console.log('dynamicEndDate', dynamicEndDate);
  // const [radius, setRadius] = useState(50);
  // const [isChildrenEvents, setChildrenEvents] = useState(isForKids);

  useLayoutEffect(() => {
    navigation.addListener('focus', e => {
      setIsTabBarDisabled(false);
    });
  }, []);

  // Simplified filter function - events are already filtered by backend based on booked/pending status
  const filterEvents = (data: any[], selectedTimeframe: {id: number; title: string}) => {
    if (!data) return [];
    
    const now = new Date();
    
    if (selectedTimeframe.title === t('home.upcoming')) {
      return data.filter((event: any) => {
        const endDate = new Date(event.end_date);
        return endDate >= now; // Future events
      });
    } else if (selectedTimeframe.title === t('home.past')) {
      return data.filter((event: any) => {
        const endDate = new Date(event.end_date);
        return endDate < now; // Past events
      });
    }
    
    return data;
  };

  interface FilterButtonsProps {
    setIsMapView: (value: boolean) => void;
    isMapView: boolean;
  }

  const FilterButtons = ({setIsMapView, isMapView}: FilterButtonsProps) => {
    return (
      <View style={{flexDirection: 'row', justifyContent: 'flex-start', marginLeft: hp('1.2%')}}>
        {/* Upcoming/Past Toggle */}
        {eventTypes.map(eventType => (
          <TouchableOpacity
            key={eventType.id}
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              backgroundColor: selectedEventType.id === eventType.id ? colors.warning : colors.gray400,
              borderRadius: 8,
              paddingHorizontal: hp('1.5%'),
              marginRight: hp('1%'),
              height: 36,
            }}
            onPress={() => setSelectedEventType(eventType)}>
            <Text style={{color: colors.white, fontSize: hp('2%')}}>
              {eventType.title}
            </Text>
          </TouchableOpacity>
        ))}

        <View style={{flex: 1}} />
      </View>
    );
  };

  const EventSearch = () => {
    const [inputValue, setInputValue] = useState('');

    useEffect(() => {
      // Update global search value when input changes
      setGlobalInputValue(inputValue);
    }, [inputValue]);

    return (
      <View style={{flex: 1, paddingHorizontal: 8}}>
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
          }}>
          <View
            style={{
              flex: 1,
              flexDirection: 'row',
              alignItems: 'center',
              paddingHorizontal: hp('1.25%'),
              margin: hp('0.5%'),
              borderRadius: 10,
              backgroundColor: colors.white,
              borderColor: colors.separatorLine,
              borderWidth: 1,
            }}>
            <Svg width={hp('2.5%')} height={hp('2.5%')} viewBox="0 0 16 16" fill="none">
              <Path
                d="M6.50301 12.8767C7.89827 12.8767 9.18492 12.4285 10.2376 11.6814L14.1978 15.616C14.3816 15.7986 14.6239 15.8899 14.8829 15.8899C15.426 15.8899 15.8103 15.4749 15.8103 14.9436C15.8103 14.6946 15.7268 14.4539 15.543 14.2795L11.6078 10.3616C12.435 9.28247 12.9279 7.94604 12.9279 6.49341C12.9279 2.98218 10.0371 0.110107 6.50301 0.110107C2.97726 0.110107 0.078125 2.97388 0.078125 6.49341C0.078125 10.0046 2.96891 12.8767 6.50301 12.8767ZM6.50301 11.4988C3.74591 11.4988 1.46503 9.23267 1.46503 6.49341C1.46503 3.75415 3.74591 1.48804 6.50301 1.48804C9.26011 1.48804 11.541 3.75415 11.541 6.49341C11.541 9.23267 9.26011 11.4988 6.50301 11.4988Z"
                fill={colors.textPrimary}
                fillOpacity="0.6"
              />
            </Svg>
            <TextInput
              style={{flex: 1, fontSize: hp('2%'), color: colors.black, marginLeft: hp('1.5%'), height: 40}}
              placeholder="Search for events..."
              placeholderTextColor={colors.placeholderText}
              underlineColorAndroid="transparent"
              value={inputValue}
              onChangeText={text => setInputValue(text)}
            />
          </View>
        </View>
      </View>
    );
  };

  const selectedEventTypeData = eventTypes.find(item => item.id === selectedEventType.id);
  // const selectedTimeframeData = timeframes.find(item => item.id === selectedTimeframe.id);

  const listComponent = useMemo(() => {
    const EventTypeComponent = selectedEventTypeData?.component(
      scrollHandler,
      filterEvents,
      selectedTimeframe,
      globalInputValue,
    );
    // const TimeframeComponent = selectedTimeframeData?.component(scrollHandler);
    // console.log(selectedTimeframe);
    return (
      <>
        {/* {TimeframeComponent} */}
        {EventTypeComponent}
      </>
    );
  }, [scrollHandler, selectedEventType, selectedTimeframe]);

  const header = useMemo(() => {
    return (
      <Animated.View
        style={[
          {
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            overflow: 'hidden',
            zIndex: 300,
            paddingTop: Platform.OS === 'ios' ? top : 10,
            backgroundColor: 'FCF9ED',
          },
          scrollAnimatedStyle,
        ]}>
        <EventSearch />
        <View style={{height: 8}} />
        <FilterButtons setIsMapView={setIsMapView} isMapView={isMapView} />
      </Animated.View>
    );
  }, [scrollAnimatedStyle, top, selectedEventType, selectedTimeframe]);

  return (
    <>
      <Animated.View
        entering={FadeInRight.duration(300)}
        style={{
          zIndex: 10000,
          position: 'absolute',
          bottom: hp('15%'),
          right: hp('2.5%'),
          backgroundColor: colors.background,
        }}
      />
      <View style={{flex: 1, backgroundColor: colors.background}}>
        {header}
        <Animated.View style={[{flex: 1, backgroundColor: colors.background}]}>{listComponent}</Animated.View>
      </View>
    </>
  );
};

export default MyEventTabs;
